<template>
  <SeoInterHubSkeleton
    v-if="seoLoading"
    :nav-section-count="3"
    show-content-section />
  <div
    v-else
    class="inter-level1">
    <PrepeersBreadcrumb
      auto
      section="ou-etudier"
      :current-label="seoInterData?.seodatas.h1" />

    <!-- ── HERO + H1 ─────────────────────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ seoInterData?.seodatas.h1 }}</h1>
        <div
          class="hero-section__intro"
          v-html="formatSeoIntro(seoInterData?.seodatas.introduction ?? '')" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── TITRE VIDÉO + VIDEO ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.seodatas.youTubeUrl"
      class="video-section">
      <div class="container">
        <h2 class="video-section__title">
          {{ seoInterData?.seodatas.h1 }} : {{ t('seointer.videoTextSuffix') }}
        </h2>
        <div class="video-section__wrapper">
          <iframe
            :src="seoInterData?.seodatas.youTubeUrl"
            title="Vidéo explicative"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            class="video-section__iframe" />
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGE PARENT ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.parsedParentBlock"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ seoInterData?.parsedParentBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in seoInterData?.parsedParentBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGES MÊME NIVEAU ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.parsedSistersBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ seoInterData?.parsedSistersBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in seoInterData?.parsedSistersBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGES ENFANTS ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.parsedChildrenBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ seoInterData?.parsedChildrenBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in seoInterData?.parsedChildrenBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── LIENS BLOCKS COUSINS ─────────────────────────────────────────────── -->
    <section
      v-for="block in seoInterData?.parsedCousinsBlocks"
      :key="block.Title"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ block.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in block.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── CTA SI NON CONNECTÉ ─────────────────────────────────────────────── -->
    <section
      v-if="!isLoggedIn"
      class="cta-section">
      <div class="container">
        <PrePeersLolaCta
          variant="blog"
          :entity-name="seoInterData?.seodatas.h1" />
      </div>
    </section>

    <!-- ── CONTENU HTML RICHE ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.seodatas.contentText"
      class="content-section">
      <div class="container">
        <UiPrepeersQlEditorContent :html="seoInterData?.seodatas.contentText ?? ''" />
      </div>
    </section>

    <!-- ── LIENS PARTNERS ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.partners.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ t('seointer.root.title_partners') }}</h2>
        <p class="media-grid__desc">{{ t('seointer.root.description_partners') }}</p>
        <div class="media-grid__cards">
          <UiSchoolCard
            v-for="school in seoInterData?.partners"
            :key="school.id"
            :school="school" />
        </div>
      </div>
    </section>

    <!-- ── LIENS PROGRAMS ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.programs.length"
      class="programs-section">
      <div class="container">
        <h2 class="programs__title">{{ t('seointer.root.title_programs') }}</h2>
        <p class="programs__desc">{{ t('seointer.root.description_programs') }}</p>
        <div class="programs__cards">
          <UiSchoolCard
            v-for="program in seoInterData?.programs"
            :key="program.id"
            :school="program" />
        </div>
      </div>
    </section>

    <!-- ── LIENS ARTICLES BLOG ─────────────────────────────────────────────── -->
    <section
      v-if="seoInterData?.articles.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ t('seointer.root.title_articles') }}</h2>
        <p class="media-grid__desc">{{ t('seointer.root.description_articles') }}</p>
        <div class="media-grid__cards">
          <UiArticleCard
            v-for="article in seoInterData?.articles"
            :key="article.id"
            :article="article" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import { computed } from 'vue'
import { useSeoInterData } from '~/composables/useSeoInterData'
import { formatSeoIntro } from '~/utils/helpers'
import PrePeersLolaCta from '~/components/ui/PrePeersLolaCta.vue'

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()

// Reconstitue le tableau de segments depuis l'URL
// Ex: /ou-etudier/france/paris → ['france', 'paris']
const pathSegments: ComputedRef<string[]> = computed(() => {
  const p: string | string[] | undefined = route.params.slug
  return Array.isArray(p) ? p : p ? [p] : []
})

const {
  seoInterData,
  loading: seoLoading,
  redirectIfDead,
} = await useSeoInterData({
  language: locale,
  pageType: 'locations',
  param1: () => pathSegments.value[0],
  param2: () => (pathSegments.value.length > 1 ? pathSegments.value[1] : ''),
})

const seoRedirect = redirectIfDead()
if (seoRedirect) await seoRedirect

// ── PAGE META ───────────────────────────────────────────────
definePageMeta({
  layout: 'default',
})

// ── API & AUTH ───────────────────────────────────────────────
const { isLoggedIn } = useAuth()

// ── SEO / HEAD ───────────────────────────────────────────────
useSeoMeta({
  title: computed(() => seoInterData.value?.seodatas.title),
  description: computed(() => seoInterData.value?.seodatas.description),
  ogTitle: computed(() => seoInterData.value?.seodatas.h1),
  ogDescription: computed(() => seoInterData.value?.seodatas.introduction),
  ogType: 'website',
})

const { canonicalUrl } = useSeo()

useJsonLd(
  computed(() =>
    collectionPageJsonLd({
      name: seoInterData.value?.seodatas.h1,
      description: seoInterData.value?.seodatas.introduction,
      url: canonicalUrl.value,
    }),
  ),
)
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   STYLES LEVEL
   ════════════════════════════════════════════════════════════ */

/* ── VARIABLES LOCALES ─────────────────────────────────────────────── */
:global(body) {
  background-color: var(--bg-base);
}

/* ── CONTENT HTML ─────────────────────────────────────────────── */
</style>
